import useMotionPreference from "@/hooks/useMotionPreference";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MagneticLink } from "@/lib/motion";
import { trackNavResumeClick } from "@/lib/analytics";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Skills", href: "#skills" },
  { label: "Experience", href: "#experience" },
  { label: "Work", href: "#projects" },
  { label: "GitHub", href: "#github" },
  { label: "Blog", href: "#blog" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const toggleRef = useRef(null);
  const reduceMotion = useMotionPreference();
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    let ticking = false;
    let frame;
    const update = () => {
      setScrolled(window.scrollY > 20);
      const marker = Math.min(window.innerHeight * 0.35, 240);
      let current = null;
      LINKS.forEach(({ href }) => {
        const section = document.getElementById(href.slice(1));
        if (section && section.getBoundingClientRect().top <= marker) current = href;
      });
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 4) current = "#contact";
      setActive(current);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        frame = requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Lazy sections and API content can change document geometry after first paint.
    const observer = new ResizeObserver(onScroll);
    observer.observe(document.documentElement);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onResize = () => {
      if (window.matchMedia("(min-width: 1024px)").matches) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  return (
    <motion.header
      data-testid="navbar"
      initial={reduceMotion ? false : { y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-white/5 bg-black/60 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <motion.div
        data-testid="scroll-progress"
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 origin-left bg-brand-500"
        style={{ scaleX: scrollYProgress }}
      />
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:px-10">
        <a href="#top" data-testid="nav-logo" className="font-display text-lg font-bold tracking-tight text-white">
          Tanishq<span className="text-brand-500">.</span>
        </a>

        <div className="hidden items-center gap-6 lg:flex lg:gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              aria-current={active === l.href ? "location" : undefined}
              className={`focus-ring relative py-2 text-sm transition-colors duration-200 hover:text-white ${active === l.href ? "text-white" : "text-white/60"}`}
            >
              {l.label}
              {active === l.href && (
                <motion.span
                  aria-hidden="true"
                  layoutId="active-nav-underline"
                  className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-brand-500"
                  transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
            </a>
          ))}
          <MagneticLink
            href="/TanishqSainiResume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-resume-cta"
            onClick={trackNavResumeClick}
            className="group inline-flex items-center gap-1.5 rounded-full bg-brand-solid px-4 py-2 text-sm font-medium text-brand-contrast transition-colors duration-200 hover:bg-brand-solid-hover"
          >
            Resume
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </MagneticLink>
        </div>

        
        <button
          ref={toggleRef}
          type="button"
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="focus-ring relative flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          {[0, 1, 2].map((line) => (
            <motion.span
              key={line}
              aria-hidden="true"
              className="absolute h-px w-[18px] rounded-full bg-current"
              animate={{ y: open ? 0 : (line - 1) * 5, rotate: open ? (line === 0 ? 45 : -45) : 0, opacity: open && line === 1 ? 0 : 1 }}
              transition={{ duration: reduceMotion ? 0 : 0.22 }}
            />
          ))}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-navigation"
            data-testid="mobile-menu"
            inert={!open}
            initial="closed"
            animate="open"
            exit="closed"
            variants={{
              closed: { height: 0, opacity: 0, transition: { duration: reduceMotion ? 0 : 0.22 } },
              open: { height: "auto", opacity: 1, transition: { duration: reduceMotion ? 0 : 0.28, staggerChildren: reduceMotion ? 0 : 0.045, delayChildren: reduceMotion ? 0 : 0.08 } },
            }}
            className="overflow-hidden border-t border-white/5 bg-black/90 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <motion.a
                  key={l.href}
                  variants={{ closed: { opacity: 0, x: reduceMotion ? 0 : -12 }, open: { opacity: 1, x: 0 } }}
                  transition={{ duration: reduceMotion ? 0 : 0.2 }}
                  href={l.href}
                  aria-current={active === l.href ? "location" : undefined}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-3 text-base text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href="/TanishqSainiResume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { setOpen(false); trackNavResumeClick(); }}
                className="mt-2 rounded-full bg-brand-solid px-4 py-3 text-center text-base font-medium text-brand-contrast"
              >
                Resume
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
